import { useEffect, useRef, useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Check, GripVertical, Plus, X } from "lucide-react";
import type { ChecklistItem, TaskWithFlags } from "@desk/contracts";
import { useTaskActions } from "@/data/actions";
import type { EnqueueResult } from "@/data/outbox";
import { cn } from "@/lib/utils";

export function TitleEditor({ task, autoFocus }: { task: TaskWithFlags; autoFocus?: boolean }) {
  const a = useTaskActions();
  const [value, setValue] = useState(task.title);
  // The draft lives in a ref too: Escape and Enter blur synchronously, before React re-renders with
  // the latest state, so the blur handler must not read `value` from a stale render.
  const draft = useRef(task.title);
  const cancelled = useRef(false);
  const latest = useRef(task);
  latest.current = task;
  const ref = useRef<HTMLTextAreaElement>(null);
  const editing = useRef(false);
  const set = (v: string) => {
    draft.current = v;
    setValue(v);
  };
  useEffect(() => {
    if (!editing.current) set(task.title);
  }, [task.title]);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  const commit = () => {
    editing.current = false;
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const t = latest.current;
    const v = draft.current.trim();
    if (!v) return set(t.title);
    if (v !== t.title) void a.update(t, { title: v });
  };
  const closed = task.status === "done" || task.status === "cancelled";
  return (
    <textarea
      ref={ref}
      aria-label="Title"
      rows={1}
      value={value}
      maxLength={500}
      onFocus={() => (editing.current = true)}
      onChange={(e) => set(e.target.value.replace(/\n/g, " "))}
      onBlur={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
        if (e.key === "Escape") {
          // Cancel: restore the saved title and make no request.
          cancelled.current = true;
          set(latest.current.title);
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
      className={cn(
        "field-sizing-content block w-full resize-none bg-transparent py-0.5 text-[16px] font-medium leading-[24px] text-foreground outline-none",
        closed && "text-muted-foreground",
      )}
    />
  );
}

export function NotesEditor({ task }: { task: TaskWithFlags }) {
  const a = useTaskActions();
  const [value, setValue] = useState(task.description);
  // Autosave timers and unmount cleanup outlive the render that scheduled them, so everything they
  // read (the draft, the task, the actions) comes from refs that always hold the latest values.
  const draft = useRef(task.description);
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(task);
  latest.current = task;
  const actions = useRef(a);
  actions.current = a;
  useEffect(() => {
    if (!dirty.current) {
      draft.current = task.description;
      setValue(task.description);
    }
  }, [task.description]);
  const flush = useRef(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (!dirty.current) return;
    dirty.current = false;
    const v = draft.current;
    if (v !== latest.current.description) void actions.current.update(latest.current, { description: v });
  }).current;
  useEffect(() => flush, [flush]);
  return (
    <textarea
      aria-label="Notes"
      placeholder="Notes"
      value={value}
      rows={1}
      onChange={(e) => {
        draft.current = e.target.value;
        setValue(e.target.value);
        dirty.current = true;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(flush, 700);
      }}
      onBlur={flush}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") (e.target as HTMLTextAreaElement).blur();
      }}
      className="field-sizing-content block min-h-7 w-full resize-none bg-transparent py-1 text-[14px] leading-[22px] text-foreground/90 outline-none placeholder:text-subtle"
    />
  );
}

function ChecklistRow({
  item,
  onToggle,
  onRename,
  onDelete,
  onMove,
}: {
  item: ChecklistItem;
  onToggle: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const s = useSortable({ id: item.id });
  const [value, setValue] = useState(item.title);
  useEffect(() => setValue(item.title), [item.title]);
  return (
    <li
      ref={s.setNodeRef}
      style={{ transform: CSS.Transform.toString(s.transform), transition: s.transition }}
      className={cn("group/ci flex h-8 items-center gap-2 rounded-md pr-1", s.isDragging && "z-10 bg-card shadow-card")}
    >
      <button
        type="button"
        {...s.attributes}
        {...s.listeners}
        aria-label={`Reorder ${item.title}`}
        className="-ml-5 inline-flex w-4 cursor-grab justify-center text-subtle opacity-0 group-hover/ci:opacity-100 focus-visible:opacity-100"
      >
        <GripVertical className="size-3.5" />
      </button>
      <button
        type="button"
        role="checkbox"
        aria-checked={item.done}
        aria-label={item.done ? `Uncheck ${item.title}` : `Check ${item.title}`}
        onClick={onToggle}
        className={cn(
          "inline-flex size-[13px] shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors",
          item.done ? "border-foreground/55 bg-foreground/55 text-background" : "border-foreground/30 hover:border-foreground/60",
        )}
      >
        {item.done && <Check className="size-2" strokeWidth={4} />}
      </button>
      <input
        aria-label="Checklist item"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const v = value.trim();
          if (!v) setValue(item.title);
          else if (v !== item.title) onRename(v);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.altKey && e.key === "ArrowUp") {
            e.preventDefault();
            onMove(-1);
          }
          if (e.altKey && e.key === "ArrowDown") {
            e.preventDefault();
            onMove(1);
          }
          if (e.key === "Backspace" && value === "") {
            e.preventDefault();
            onDelete();
          }
        }}
        className={cn(
          "h-7 min-w-0 flex-1 bg-transparent text-[14px] outline-none",
          item.done && "text-muted-foreground line-through decoration-foreground/25",
        )}
      />
      <button
        type="button"
        aria-label={`Delete ${item.title}`}
        onClick={onDelete}
        className="inline-flex size-6 items-center justify-center rounded text-subtle opacity-0 hover:bg-hover hover:text-foreground focus-visible:opacity-100 group-hover/ci:opacity-100"
      >
        <X className="size-3.5" />
      </button>
    </li>
  );
}

const sortedChecklist = (t: TaskWithFlags) => [...t.checklist].sort((x, y) => x.position - y.position);

export function ChecklistEditor({ task }: { task: TaskWithFlags }) {
  const a = useTaskActions();
  const [items, setItems] = useState<ChecklistItem[]>(() => sortedChecklist(task));
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const latest = useRef(task);
  latest.current = task;
  useEffect(() => setItems(sortedChecklist(task)), [task.checklist]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Local edits are optimistic; a failed save puts the last confirmed checklist back. */
  const guard = (p: Promise<EnqueueResult>) =>
    void p.then((r) => {
      if (r.status === "failed") setItems(sortedChecklist(latest.current));
    });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const reorder = (next: ChecklistItem[]) => {
    setItems(next);
    guard(a.checklistReorder(task, next.map((i) => i.id)));
  };
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = items.findIndex((i) => i.id === e.active.id);
    const to = items.findIndex((i) => i.id === e.over!.id);
    reorder(arrayMove(items, from, to));
  };
  const add = () => {
    const title = draft.trim();
    if (!title) return;
    setItems((xs) => [...xs, { id: `tmp-${crypto.randomUUID()}`, title, done: false, position: Number.MAX_SAFE_INTEGER }]);
    setDraft("");
    guard(a.checklistAdd(task, title));
  };
  const done = items.filter((i) => i.done).length;
  return (
    <div className="pb-1">
      {items.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul aria-label={`Checklist, ${done} of ${items.length} done`} className="flex flex-col">
              {items.map((item, idx) => (
                <ChecklistRow
                  key={item.id}
                  item={item}
                  onToggle={() => {
                    setItems((xs) => xs.map((x) => (x.id === item.id ? { ...x, done: !x.done } : x)));
                    guard(a.checklistUpdate(task, item.id, { done: !item.done }));
                  }}
                  onRename={(title) => guard(a.checklistUpdate(task, item.id, { title }))}
                  onDelete={() => {
                    setItems((xs) => xs.filter((x) => x.id !== item.id));
                    guard(a.checklistDelete(task, item.id));
                  }}
                  onMove={(dir) => {
                    const to = idx + dir;
                    if (to < 0 || to >= items.length) return;
                    reorder(arrayMove(items, idx, to));
                  }}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
      {adding || items.length > 0 ? (
        <div className="flex h-8 items-center gap-2">
          <Plus className="size-[13px] text-subtle" aria-hidden />
          <input
            aria-label="Add checklist item"
            placeholder="Add item"
            value={draft}
            autoFocus={adding && items.length === 0}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              if (!draft.trim()) setAdding(false);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
              if (e.key === "Escape") (e.target as HTMLInputElement).blur();
            }}
            className="h-7 min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-subtle"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex h-7 items-center gap-2 rounded-md text-[13.5px] text-subtle hover:text-foreground"
        >
          <Plus className="size-[15px]" />
          Checklist
        </button>
      )}
    </div>
  );
}
